import React from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ADDIS_OFFSET_MS } from '../../common/exportUtils';

// Date and time entry in Addis Ababa time on a 12-hour clock (owner rule:
// never <input type="datetime-local">, never a 24-hour clock, whatever the
// computer's time zone). Emits absolute epoch ms. The same field as the
// gift end in GeneralSettings and the event times in CityEvents.

const HOURS12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

/** Addis wall-clock parts of an instant: { y, m (1-12), d, h (0-23), mi }. */
export const msToAddisParts = (ms) => {
  const d = new Date((Number(ms) || 0) + ADDIS_OFFSET_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes() };
};

/** The instant of Addis wall-clock parts. */
export const addisPartsToMs = (p) => Date.UTC(p.y, p.m - 1, p.d, p.h || 0, p.mi || 0) - ADDIS_OFFSET_MS;

const validMs = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : /^\d{9,}$/.test(String(v).trim()) ? Number(v) : Date.parse(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 *   <AddisDateTimeField id="promo-end" value={endMs} onChange={setEndMs} />
 *   <AddisDateTimeField id="day" value={ms} onChange={setMs} dateOnly />
 *
 * value: epoch ms (number or digit string) or empty. A date picked before
 * any time is set gets defaultTime ({ h: 23, mi: 59 } = 11:59 pm).
 * dateOnly: just the date (emits the start of that Addis day, or the
 * defaultTime when given).
 */
export default function AddisDateTimeField({ id, value, onChange, disabled = false, error = false, describedBy, dateOnly = false, defaultTime, minuteStep, sx }) {
  const { t } = useTranslation();
  const ms = validMs(value);
  const p = ms ? msToAddisParts(ms) : null;
  const fallback = defaultTime || (dateOnly ? { h: 0, mi: 0 } : { h: 23, mi: 59 });
  const pm = p ? p.h >= 12 : fallback.h >= 12;
  const h12 = p ? (p.h % 12 === 0 ? 12 : p.h % 12) : fallback.h % 12 === 0 ? 12 : fallback.h % 12;
  const minute = p ? p.mi : fallback.mi;
  const steps = Number.isInteger(minuteStep) && minuteStep > 0 ? Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep).filter((m) => m < 60) : MINUTES;
  const minutes = steps.includes(minute) ? steps : steps.concat([minute]).sort((a, b) => a - b);
  const emit = (patch) => {
    const next = { h: fallback.h, mi: fallback.mi, ...(p || {}), ...patch };
    if (!next.y) return;
    onChange(addisPartsToMs(next));
  };
  const timeDisabled = disabled || !p;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, ...(sx || {}) }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={p ? dayjs(new Date(p.y, p.m - 1, p.d)) : null}
          onChange={(v) => {
            if (v && typeof v.isValid === 'function' && v.isValid()) emit({ y: v.year(), m: v.month() + 1, d: v.date() });
          }}
          format="D MMM YYYY"
          disabled={disabled}
          slotProps={{
            textField: {
              id,
              size: 'small',
              error: Boolean(error),
              sx: { width: 196 },
              inputProps: { 'aria-describedby': describedBy },
            },
          }}
        />
      </LocalizationProvider>
      {dateOnly ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            select
            size="small"
            value={h12}
            disabled={timeDisabled}
            onChange={(e) => emit({ h: (Number(e.target.value) % 12) + (pm ? 12 : 0) })}
            inputProps={{ 'aria-label': t('hour', { defaultValue: 'Hour' }) }}
            sx={{ width: 76 }}
          >
            {HOURS12.map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </TextField>
          <Typography component="span" aria-hidden="true" sx={{ px: 0.25, fontWeight: 600 }}>
            :
          </Typography>
          <TextField
            select
            size="small"
            value={minute}
            disabled={timeDisabled}
            onChange={(e) => emit({ mi: Number(e.target.value) })}
            inputProps={{ 'aria-label': t('minute', { defaultValue: 'Minute' }) }}
            sx={{ width: 76 }}
          >
            {minutes.map((mi) => (
              <MenuItem key={mi} value={mi}>
                {pad2(mi)}
              </MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={pm ? 'pm' : 'am'}
            disabled={timeDisabled}
            onChange={(e, v) => {
              if (v) emit({ h: (h12 % 12) + (v === 'pm' ? 12 : 0) });
            }}
            sx={{ ml: 0.5 }}
          >
            <ToggleButton value="am" sx={{ px: 1.5, height: 40 }}>
              am
            </ToggleButton>
            <ToggleButton value="pm" sx={{ px: 1.5, height: 40 }}>
              pm
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  );
}
